import React from 'react';

import { Checkbox } from 'primereact/checkbox';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { DataTableFilterMetaData } from 'primereact/datatable';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import { clearFilter } from 'utils';
import { resolveTooltipPosition, TooltipPosition } from 'utils';

import { PriorityOptions } from '../../types/interfaces';
import { DropdownSelectorType } from '../../types/interfaces';

import { COLUMN_CONFIG } from './COLUMN_CONFIG';
import { ExecutorsBodyTemplate } from './columnsTemplates';
import { PriorityBodyTemplate } from './columnsTemplates';

const getFilterPlaceholder = (col: any): string => {
  const placeholders: Record<string, string> = {
    id: 'Поиск по ID',
    name: 'Поиск по названию',
    taskType: 'Поиск по типу наряда',
    taskStatus: 'Поиск по статусу',
    assetName: 'Поиск по объекту',
    departmentName: 'Поиск по участку',
    employees: 'Поиск по исполнителям',
    creationDate: 'Поиск по дате создания',
    actualStartDate: 'Поиск по фактическому началу',
    plannedStartDate: 'Поиск по плановому началу',
    plannedEndDate: 'Поиск по плановому окончанию',
    actualEndDate: 'Поиск по фактическому окончанию',
    priority: 'Поиск по приоритету'
  };

  return (
    placeholders[col.field] || `Поиск по ${col.header?.toLowerCase() || ''}`
  );
};

interface TaskColumnsProps {
  clearArrayFilter: (filter: string) => void;
  visibleColumns: any[];
  handleInputChange: (
    filter: string
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMultiselectFilterChange: (
    filterKey: string,
    selectedValues: DropdownSelectorType[],
    setFilterState: React.Dispatch<React.SetStateAction<DropdownSelectorType[]>>
  ) => void;
  selectedTaskTypeFilter: DropdownSelectorType[];
  setSelectedTaskTypeFilter: React.Dispatch<
    React.SetStateAction<DropdownSelectorType[]>
  >;
  selectedStatusFilter: DropdownSelectorType[];
  setSelectedStatusFilter: React.Dispatch<
    React.SetStateAction<DropdownSelectorType[]>
  >;
  selectedPriorityFilter: DropdownSelectorType[];
  setSelectedPriorityFilter: React.Dispatch<
    React.SetStateAction<DropdownSelectorType[]>
  >;
  taskTypes: DropdownSelectorType[];
  taskStatuses: DropdownSelectorType[];
  filters: Record<string, DataTableFilterMetaData>;
  setFilters: React.Dispatch<
    React.SetStateAction<Record<string, DataTableFilterMetaData>>
  >;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  setFirst: React.Dispatch<React.SetStateAction<number>>;
  idFilterRef: React.RefObject<HTMLInputElement>;
  taskNameFilterRef: React.RefObject<HTMLInputElement>;
  departmentNameFilterRef: React.RefObject<HTMLInputElement>;
  employeesFilterRef: React.RefObject<HTMLInputElement>;
  assetFilterRef: React.RefObject<HTMLInputElement>;
  creationDateFilterRef: React.RefObject<HTMLInputElement>;
  actualStartDateFilterRef: React.RefObject<HTMLInputElement>;
  plannedStartDateFilterRef: React.RefObject<HTMLInputElement>;
  plannedEndDateFilterRef: React.RefObject<HTMLInputElement>;
  actualEndDateFilterRef: React.RefObject<HTMLInputElement>;
  columnOrder?: string[];
  columnWidths?: Record<string, string>;
  setStatusFilterType: React.Dispatch<
    React.SetStateAction<'all' | 'closed' | 'open'>
  >;
  statusFilterType: 'all' | 'closed' | 'open';
  idTooltipPosition: TooltipPosition;
  setIdTooltipPosition: React.Dispatch<React.SetStateAction<TooltipPosition>>;
}

// Ширина зависит только от текста заголовка и захардкоженных ниже параметров
// шрифта, поэтому измеряем каждый заголовок один раз. Без кэша чтение
// offsetWidth форсировало синхронный layout на каждый рендер таблицы.
const minWidthCache = new Map<string, string>();

const calculateMinWidth = (headerText: string): string => {
  const cached = minWidthCache.get(headerText);
  if (cached !== undefined) {
    return cached;
  }

  const tempElement = document.createElement('span');
  tempElement.style.visibility = 'hidden';
  tempElement.style.position = 'absolute';
  tempElement.style.whiteSpace = 'nowrap';
  tempElement.style.fontSize = '14px';
  tempElement.style.fontFamily =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  tempElement.style.fontWeight = '600';
  tempElement.textContent = headerText;

  document.body.appendChild(tempElement);
  const textWidth = tempElement.offsetWidth;
  document.body.removeChild(tempElement);

  const gapInPx = 8;
  const iconsWidth = 49;
  const totalWidth = Math.ceil(textWidth + iconsWidth + gapInPx);

  const result = `${Math.max(totalWidth, 80)}px`;
  minWidthCache.set(headerText, result);

  return result;
};

const DATE_FIELDS = new Set([
  'actualStartDate',
  'plannedStartDate',
  'plannedEndDate',
  'actualEndDate'
]);

const TASK_ID_TOOLTIP_STRATEGY = {
  defaultPosition: 'top' as const,
  fallbackPosition: 'mouse' as const,
  minOffset: 156
};

export const getTaskColumns = ({
  clearArrayFilter,
  visibleColumns,
  handleInputChange,
  handleMultiselectFilterChange,
  selectedTaskTypeFilter,
  setSelectedTaskTypeFilter,
  selectedStatusFilter,
  setSelectedStatusFilter,
  selectedPriorityFilter,
  setSelectedPriorityFilter,
  taskTypes,
  taskStatuses,
  filters,
  setFilters,
  setPageNumber,
  setFirst,
  idFilterRef,
  taskNameFilterRef,
  departmentNameFilterRef,
  employeesFilterRef,
  assetFilterRef,
  creationDateFilterRef,
  actualStartDateFilterRef,
  plannedStartDateFilterRef,
  plannedEndDateFilterRef,
  actualEndDateFilterRef,
  columnOrder = [],
  columnWidths = {},
  setStatusFilterType,
  statusFilterType,
  idTooltipPosition,
  setIdTooltipPosition
}: TaskColumnsProps) => {
  let sortedVisibleColumns = COLUMN_CONFIG.filter((col) =>
    visibleColumns.some((visibleCol) => visibleCol.field === col.field)
  );

  if (columnOrder.length > 0) {
    const orderMap = new Map(columnOrder.map((field, index) => [field, index]));
    sortedVisibleColumns = sortedVisibleColumns.sort((a, b) => {
      const aIndex = orderMap.get(a.field) ?? Infinity;
      const bIndex = orderMap.get(b.field) ?? Infinity;
      return aIndex - bIndex;
    });
  }

  const resolveIdTooltipPosition = (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ) => {
    const datatableWrapper = e.currentTarget
      .closest('.p-datatable-wrapper')
      ?.getBoundingClientRect();

    const position = resolveTooltipPosition({
      triggerRect: e.currentTarget.getBoundingClientRect(),
      boundaryRect: datatableWrapper,
      ...TASK_ID_TOOLTIP_STRATEGY
    });

    setIdTooltipPosition(position);
  };

  const getBodyTemplate = (col: any, data: any) => {
    switch (col.field) {
      case 'id':
        return (
          <div>
            <span
              id={`tooltip-id-${data.id}`}
              onMouseEnter={resolveIdTooltipPosition}
              style={{ marginRight: '4px' }}
            >
              {data.id}
            </span>
            <Tooltip
              target={`#tooltip-id-${data.id}`}
              content={'Внутренний номер наряда'}
              position={idTooltipPosition}
            />
            {data.externalId && (
              <>
                <span
                  id={`tooltip-externalId-${data.id}`}
                  onMouseEnter={resolveIdTooltipPosition}
                  style={{
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                    display: 'inline-block'
                  }}
                >
                  ({data.externalId})
                </span>
                <Tooltip
                  target={`#tooltip-externalId-${data.id}`}
                  content={'Внешний номер наряда'}
                  position={idTooltipPosition}
                />
              </>
            )}
          </div>
        );

      case 'name':
        return data.name;

      case 'taskType':
        return data.taskType;

      case 'taskStatus':
        return (
          <div>
            {typeof data.taskStatus === 'string' && data.taskStatus}.&nbsp;
            {data.closeCode as unknown as string}
            {data.isClosed && (
              <i
                className="pi pi-exclamation-circle closing-status-tooltip"
                style={{
                  fontSize: '1rem',
                  color: 'rgb(255 168 0)',
                  cursor: 'pointer',
                  marginLeft: 10
                }}
                id={`tooltip-closed-${data.id}`}
              />
            )}
            <Tooltip
              target={`#tooltip-closed-${data.id}`}
              content={'Наряд находится в закрывающем статусе'}
              position="top"
            />
          </div>
        );

      case 'assetName':
        return data.assetName;

      case 'employees':
        return <ExecutorsBodyTemplate employees={data.employees} />;

      case 'creationDate':
        return data.creationDate;

      case 'actualStartDate':
        return data.actualStartDate;

      case 'plannedStartDate':
        return data.plannedStartDate;

      case 'plannedEndDate':
        return data.plannedEndDate;

      case 'actualEndDate':
        return data.actualEndDate;

      case 'priority':
        return <PriorityBodyTemplate priority={data.priority} />;

      default:
        return data[col.field];
    }
  };

  const getFilterElement = (
    col: any,
    options: ColumnFilterElementTemplateOptions
  ) => {
    switch (col.filterField ?? col.field) {
      case 'taskType':
        return (
          <div className="flex justify-content-end">
            <MultiSelect
              value={selectedTaskTypeFilter}
              onChange={(e) => {
                setPageNumber(1);
                setFirst(0);
                handleMultiselectFilterChange(
                  'taskType',
                  e.value ?? [],
                  setSelectedTaskTypeFilter
                );
              }}
              options={taskTypes}
              optionLabel="label"
              display="chip"
              filter
              placeholder="Поиск по типу наряда"
              fixedPlaceholder
              className="flex-grow-1 flex-shrink-1"
              style={{ width: '182px' }}
            />
          </div>
        );

      case 'taskStatus':
        return (
          <div
            className="flex justify-content-end gap-3"
            style={{ flexDirection: 'column' }}
          >
            <MultiSelect
              value={selectedStatusFilter}
              onChange={(e) => {
                setPageNumber(1);
                setFirst(0);
                handleMultiselectFilterChange(
                  'taskStatus',
                  e.value ?? [],
                  setSelectedStatusFilter
                );
              }}
              display="chip"
              options={taskStatuses}
              optionLabel="label"
              filter
              placeholder="Поиск по статусу"
              fixedPlaceholder
              className="flex-grow-1 flex-shrink-1"
              style={{ width: '182px' }}
            />
            <span className="flex align-items-center gap-2" key="allStatuses">
              <Checkbox
                inputId="allStatuses"
                onChange={() => {
                  setStatusFilterType('all');
                  setPageNumber(1);
                  setFirst(0);
                }}
                checked={statusFilterType === 'all'}
              />
              <label
                htmlFor="allStatuses"
                className="p-checkbox-label font-normal"
              >
                Наряды во всех статусах
              </label>
            </span>
            <span className="flex align-items-center gap-2" key="closedTasks">
              <Checkbox
                inputId="closedTasks"
                onChange={() => {
                  setStatusFilterType('closed');
                  setPageNumber(1);
                  setFirst(0);
                }}
                checked={statusFilterType === 'closed'}
              />
              <label
                htmlFor="closedTasks"
                className="p-checkbox-label font-normal"
              >
                Закрытые наряды
              </label>
            </span>
            <span className="flex align-items-center gap-2" key="openTasks">
              <Checkbox
                inputId="openTasks"
                onChange={() => {
                  setStatusFilterType('open');
                  setPageNumber(1);
                  setFirst(0);
                }}
                checked={statusFilterType === 'open'}
              />
              <label
                htmlFor="openTasks"
                className="p-checkbox-label font-normal"
              >
                Открытые наряды
              </label>
            </span>
          </div>
        );

      case 'priority':
        return (
          <div className="flex justify-content-end">
            <MultiSelect
              value={selectedPriorityFilter}
              onChange={(e) => {
                setPageNumber(1);
                setFirst(0);
                handleMultiselectFilterChange(
                  'priority',
                  e.value ?? [],
                  setSelectedPriorityFilter
                );
              }}
              options={PriorityOptions}
              optionLabel="label"
              display="chip"
              filter
              placeholder="Поиск по приоритету"
              fixedPlaceholder
              className="flex-grow-1 flex-shrink-1"
              style={{ width: '182px' }}
            />
          </div>
        );

      default:
        return null;
    }
  };
  const handleFilterClear = (field: string) => {
    switch (field) {
      case 'id':
        clearFilter(
          'id',
          filters,
          setFilters,
          idFilterRef,
          setPageNumber,
          setFirst
        );
        break;

      case 'name':
        clearFilter(
          'name',
          filters,
          setFilters,
          taskNameFilterRef,
          setPageNumber,
          setFirst
        );
        break;

      case 'taskType':
        clearArrayFilter('taskType');
        break;

      case 'taskStatus':
        clearArrayFilter('taskStatus');
        setStatusFilterType('all');
        break;

      case 'assetName':
        clearFilter(
          'assetName',
          filters,
          setFilters,
          assetFilterRef,
          setPageNumber,
          setFirst
        );
        break;

      case 'departmentName':
        clearFilter(
          'departmentName',
          filters,
          setFilters,
          undefined,
          setPageNumber,
          setFirst
        );
        break;

      case 'employees':
        clearFilter(
          'employees',
          filters,
          setFilters,
          undefined,
          setPageNumber,
          setFirst
        );
        break;

      case 'creationDate':
        clearFilter(
          'creationDate',
          filters,
          setFilters,
          creationDateFilterRef,
          setPageNumber,
          setFirst
        );
        break;

      case 'actualStartDate':
        clearFilter(
          'actualStartDate',
          filters,
          setFilters,
          actualStartDateFilterRef,
          setPageNumber,
          setFirst
        );
        break;

      case 'plannedStartDate':
        clearFilter(
          'plannedStartDate',
          filters,
          setFilters,
          plannedStartDateFilterRef,
          setPageNumber,
          setFirst
        );
        break;

      case 'plannedEndDate':
        clearFilter(
          'plannedEndDate',
          filters,
          setFilters,
          plannedEndDateFilterRef,
          setPageNumber,
          setFirst
        );
        break;

      case 'actualEndDate':
        clearFilter(
          'actualEndDate',
          filters,
          setFilters,
          actualEndDateFilterRef,
          setPageNumber,
          setFirst
        );
        break;

      case 'priority':
        clearArrayFilter('priority');
        break;

      default:
        console.warn('Unknown field: ', field);
    }
  };

  return sortedVisibleColumns.map((col) => {
    const width = columnWidths[col.field] || col.width;
    const shouldShowCustomFilter =
      col.field === 'priority' ||
      col.field === 'taskType' ||
      col.field === 'taskStatus';

    const isDateColumn = DATE_FIELDS.has(col.field);
    const calculatedMinWidth = isDateColumn
      ? undefined
      : calculateMinWidth(col.header || '');

    let minWidthValue = isDateColumn ? col.minWidth : calculatedMinWidth;

    if (!minWidthValue) {
      minWidthValue = col.minWidth || calculatedMinWidth || '80px';
    }

    if (!isDateColumn && col.minWidth && calculatedMinWidth) {
      const calculatedNum = parseInt(calculatedMinWidth);
      const configNum = parseInt(col.minWidth);
      if (!isNaN(calculatedNum) && !isNaN(configNum)) {
        minWidthValue =
          calculatedNum > configNum ? calculatedMinWidth : col.minWidth;
      } else {
        minWidthValue = col.minWidth;
      }
    }

    return (
      <Column
        key={col.field}
        columnKey={col.field}
        sortable
        sortField={col.field}
        field={col.field}
        header={col.header}
        headerClassName={isDateColumn ? 'tasks-date-header' : undefined}
        body={(data) => getBodyTemplate(col, data)}
        style={{ width, flexGrow: 1, minWidth: minWidthValue }}
        filterField={col.field}
        filter
        showAddButton={false}
        filterPlaceholder={getFilterPlaceholder(col)}
        filterElement={
          shouldShowCustomFilter
            ? (options) => getFilterElement(col, options)
            : undefined
        }
        onFilterClear={() => handleFilterClear(col.field)}
        showFilterMatchModes={!shouldShowCustomFilter}
      />
    );
  });
};
